<template>
  <div class="relative">
    <Spinner
      v-if="loading"
      :color="'primary'"
      :size="'5rem'"
      :width="'0.5rem'"
      class="absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-50"
    />

    <div class="flex mb-4">
      <div class="flex flex-col flex-1">
        <div class="mb-2">
          <div class="flex flex-col">
            <div v-if="!isEditing" class="flex items-center gap-2">
              <div>
                <div class="flex items-center">
                  <h1 class="font-bold text-left text-black text-2xl">
                    {{ tempPolicyName }}
                  </h1>
                  <v-icon
                    :icon="mdiPencilOutline"
                    class="cursor-pointer text-primary ml-1"
                    size="20"
                    @click="startEditing"
                  />
                </div>

                <p class="text-start text-sm" v-if="mode === 'edit'">Em {{ step4.name }}</p>
                <p v-if="errors?.name" class="text-red-500 text-xs mt-1">{{ errors.name }}</p>
              </div>
            </div>

            <div v-else class="flex w-2/4 items-center gap-4">
              <input
                v-model="tempPolicyName"
                ref="nameInput"
                class="border rounded-md px-2 py-1 w-full"
                :class="errors?.name ? 'border-red-500' : 'border-gray-300'"
                placeholder="Nome da política de pagamento"
                @keydown.enter.prevent="saveEdit"
                @keydown.esc.prevent="cancelEdit"
                @blur="saveEdit"
                @input="onClearError?.('name')"
              />
              <div class="flex gap-4">
                <v-icon
                  :icon="mdiCheck"
                  class="cursor-pointer text-green-600"
                  size="30"
                  @click="saveEdit"
                />
                <v-icon
                  :icon="mdiClose"
                  class="cursor-pointer text-red-600"
                  size="30"
                  @click="cancelEdit"
                />
              </div>
            </div>
          </div>
        </div>

        <p class="text-sm text-left font-bold text-black my-2" v-if="mode === 'create'">
          Vigência do vínculo entre escala e política de pagamento
        </p>

        <PolicyVigencyFields
          v-if="mode === 'create'"
          v-model:start-date="step4.startDate"
          v-model:end-date="step4.endDate"
          v-model:indefinite-end-date="step4.indefiniteEndDate"
          :max-date="scheduleMaxDate"
          :errors="errors"
          testid-prefix="create-schedule-policy-vigency"
          @clear-error="(f) => onClearError?.(f)"
        />
      </div>

      <div class="flex self-end h-[56px] mt-8 sm:mt-0">
        <outlined-button
          @click="store.addRule()"
          :text="'Adicionar regra'"
          height="32px"
          :append-icon="mdiPlus"
          class="text-sm !w-auto !px-4 !py-1"
        />
      </div>
    </div>

    <PaymentPolicyRulesTable
      :rules="step4.rules"
      :shifts="step2.shifts"
      :positions="step2.positions"
      :teams="step2.teams"
      :days="step2.days"
      :formations="step2.formations"
      :formationRequired="shouldShowFormation"
      :repassModes="repassModes"
      :confirmDelete="confirmDeleteRule"
      :copyRule="copyRule"
      :errors="errors"
    />
    <p v-if="errors?.rules" class="text-red-500 text-xs mt-2">{{ errors.rules }}</p>

    <modal-vue @close="showModalDelete = false" :modalActive="showModalDelete">
      <template #header>
        <p class="font-bold">Excluir regra da política de pagamento</p>
      </template>

      <p class="text-base font-normal">
        Tem certeza que deseja excluir esta regra?<br />Essa ação não poderá ser desfeita!
      </p>

      <template #footer>
        <div class="flex justify-center gap-4">
          <OutlinedButton width="119px" @click="showModalDelete = false" text="Cancelar" />
          <filled-button
            :loading="isLoading"
            text="Excluir"
            width="119px"
            @click="handleDeletePaymentPolicy"
          />
        </div>
      </template>
    </modal-vue>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { PAYMENT_TEAM_OPTIONS, useCreateScheduleStore } from '@/pinia-store/useCreateScheduleStore'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { useScheduleMaxDate } from '@/hooks/useScheduleMaxDate'
import Spinner from '@/components/others/Spinner.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import ModalVue from '@/components/designSystem/modals/Modal.vue'

import { mdiPlus, mdiPencilOutline, mdiCheck, mdiClose } from '@mdi/js'
import PaymentPolicyRulesTable from './PaymentPolicyRulesTable.vue'
import PolicyVigencyFields from './PolicyVigencyFields.vue'
import Network from '@/Network'
import { weekDays } from '@/utils/date'

const props = defineProps<{
  mode: 'create' | 'edit'
  formationRequired?: boolean
  scheduleId?: number
  errors?: Record<string, string>
  onClearError?: (field: string) => void
}>()

const store = useCreateScheduleStore()
const { step1, step4, step2 } = storeToRefs(store)

const scheduleEndDateStr = computed(() => step1.value.endDate?.toISOString() ?? null)
const { scheduleMaxDate } = useScheduleMaxDate(scheduleEndDateStr)

const shouldShowFormation = computed(() => {
  return step2.value.formationRequired || props.formationRequired === true
})

const toast = useToast()
const { notifyError } = useErrorToast()
const loading = ref(false)
const tempPolicyName = ref('')
const isEditing = ref(props.mode === 'create')
const nameInput = ref<HTMLInputElement | null>(null)
const showModalDelete = ref(false)
const ruleIndexToDelete = ref<number | null>(null)
const isLoading = ref(false)

const repassModes = [
  { label: 'À vista', value: 'IMMEDIATE' },
  { label: 'Fluxo', value: 'FLOW' }
]

const loadOptions = async () => {
  store.setTeams(PAYMENT_TEAM_OPTIONS)
  store.setDays(weekDays)
}

const load = async () => {
  if (!props.scheduleId) return

  loading.value = true
  try {
    const data = (await Network.schedule.getById(props.scheduleId)) as any

    store.setPolicyName(data.name)
    step4.value.indefiniteEndDate = data.indefiniteEndDate ?? false
    step4.value.rules = data.rules ?? []

    tempPolicyName.value = data.name
    store.setShifts(data.shifts ?? [])
    store.setPositions(data.positions ?? [])
    store.setFormations(data.formations ?? [])
  } catch (err) {
    notifyError(err, 'Não foi possível carregar os dados da escala.')
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await loadOptions()
  if (props.mode === 'edit') {
    await load()
  } else {
    nextTick(() => {
      nameInput.value?.focus()
    })
  }
})

watch(
  () => step4.value.name,
  (newName) => {
    if (!isEditing.value) {
      tempPolicyName.value = newName
    }
  }
)

const confirmDeleteRule = (index: number) => {
  ruleIndexToDelete.value = index
  showModalDelete.value = true
}

const handleDeletePaymentPolicy = async () => {
  if (ruleIndexToDelete.value === null) return

  isLoading.value = true
  try {
    store.removeRule(ruleIndexToDelete.value)
    toast.success('Política de plantão excluída com sucesso!')
  } catch (err) {
    toast.error('Erro ao excluir a política.')
  } finally {
    showModalDelete.value = false
    isLoading.value = false
    ruleIndexToDelete.value = null
  }
}

const copyRule = async (index: number) => {
  store.copyRule(index)
  toast.success('Regra duplicada com sucesso!')
}

const startEditing = async () => {
  tempPolicyName.value = step4.value.name
  isEditing.value = true
  await nextTick()
  nameInput.value?.focus()
}

const saveEdit = () => {
  if (!tempPolicyName.value.trim()) {
    toast.error('Nome inválido.')
    return
  }
  store.setPolicyName(tempPolicyName.value)
  isEditing.value = false
  props.onClearError?.('name')
}

const cancelEdit = () => {
  tempPolicyName.value = step4.value.name
  isEditing.value = false
}

const validateAndConfirmName = (): boolean => {
  if (!isEditing.value) return true
  if (!tempPolicyName.value.trim()) return false
  store.setPolicyName(tempPolicyName.value)
  isEditing.value = false
  props.onClearError?.('name')
  return true
}

defineExpose({ validateAndConfirmName })
</script>
